import React, {
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  ClipboardEvent,
} from "react";
import { Box, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import i18n from "localization/i18n";
import { buildTestId } from "helpers/testId";

interface NumberInputFieldsProps {
  /**
   * Number of input fields (default: 4)
   */
  fieldCount?: number;

  /**
   * Callback fired when all fields are filled
   */
  onComplete?: (value: string) => void;

  /**
   * Callback fired on value change
   */
  onChange?: (value: string) => void;

  /**
   * Custom sx props for the container
   */
  sx?: SxProps<Theme>;

  /**
   * Whether to mask the input (show asterisks instead of numbers)
   */
  masked?: boolean;

  /**
   * Whether fields are disabled
   */
  disabled?: boolean;

  /**
   * Variant of the TextField (default: 'outlined')
   */
  variant?: "outlined" | "filled" | "standard";

  /**
   * Size of the TextField (default: 'medium')
   */
  size?: "small" | "medium";

  isError?: boolean;

  /**
   * DOM contract prefix, defaulting to `number-input`: each digit `<input>` is
   * `${testId}-<index>` (0-based) and the row itself is `${testId}`. Filling
   * the last field auto-submits through `onComplete` - there is no button.
   */
  testId?: string;
}

export const NumberInputFields: React.FC<NumberInputFieldsProps> = ({
  fieldCount = 4,
  onComplete,
  onChange,
  sx = {},
  masked = false,
  disabled = false,
  variant = "outlined",
  size = "medium",
  isError = false,
  testId = "number-input",
}) => {
  const [values, setValues] = useState<string[]>(Array(fieldCount).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  /**
   * Clear all fields when isError becomes true
   */
  useEffect(() => {
    if (isError) {
      const emptyValues = Array(fieldCount).fill("");
      setValues(emptyValues);

      // Call onChange callback with empty string
      if (onChange) {
        onChange("");
      }

      // Focus on the first field
      inputRefs.current[0]?.focus();
    }
  }, [isError, fieldCount, onChange]);

  /**
   * Get the next field index based on direction
   */
  const getNextIndex = (currentIndex: number): number => {
    return currentIndex + 1;
  };

  /**
   * Get the previous field index based on direction
   */
  const getPrevIndex = (currentIndex: number): number => {
    return currentIndex - 1;
  };

  /**
   * Check if index is valid
   */
  const isValidIndex = (index: number): boolean => {
    return index >= 0 && index < fieldCount;
  };

  /**
   * Handle input change
   */
  const handleChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    // Only accept single digit numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Call onChange callback
    if (onChange) {
      onChange(newValues.join(""));
    }

    // Auto-advance to next field if value is entered
    if (value && isValidIndex(getNextIndex(index))) {
      inputRefs.current[getNextIndex(index)]?.focus();
    }

    // Check if all fields are filled
    if (value && newValues.every((v) => v !== "")) {
      if (onComplete) {
        onComplete(newValues.join(""));
      }
    }
  };

  /**
   * Handle key down events
   */
  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle backspace
    if (event.key === "Backspace") {
      const currentValue = values[index];

      // If current field is empty, move to previous field
      if (!currentValue && isValidIndex(getPrevIndex(index))) {
        event.preventDefault();
        const prevIndex = getPrevIndex(index);
        inputRefs.current[prevIndex]?.focus();

        // Clear the previous field
        const newValues = [...values];
        newValues[prevIndex] = "";
        setValues(newValues);

        if (onChange) {
          onChange(newValues.join(""));
        }
      }
    }

    // Handle left/right arrow keys
    if (event.key === "ArrowLeft") {
      const prevIndex = getPrevIndex(index);
      if (isValidIndex(prevIndex)) {
        inputRefs.current[prevIndex]?.focus();
      }
    }

    if (event.key === "ArrowRight") {
      const nextIndex = getNextIndex(index);
      if (isValidIndex(nextIndex)) {
        inputRefs.current[nextIndex]?.focus();
      }
    }
  };

  /**
   * Handle paste event
   */
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text/plain");

    // Only accept numeric values
    const numericData = pastedData.replace(/\D/g, "");

    if (numericData) {
      const newValues = [...values];
      const chars = numericData.split("").slice(0, fieldCount);

      chars.forEach((char, idx) => {
        newValues[idx] = char;
      });

      setValues(newValues);

      if (onChange) {
        onChange(newValues.join(""));
      }

      // Focus on the next empty field or the last field
      const nextEmptyIndex = newValues.findIndex((v) => v === "");
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[fieldCount - 1]?.focus();
      }

      // Check if all fields are filled
      if (newValues.every((v) => v !== "")) {
        if (onComplete) {
          onComplete(newValues.join(""));
        }
      }
    }
  };

  /**
   * Handle focus - select all text
   */
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  /**
   * Render input fields
   */
  const renderFields = () => {
    const fields = [];

    for (let i = 0; i < fieldCount; i++) {
      fields.push(
        <TextField
          key={i}
          error={isError}
          inputRef={(el) => (inputRefs.current[i] = el)}
          type={masked ? "password" : "text"}
          inputMode="numeric"
          value={values[i]}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(i, e)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(i, e)
          }
          onPaste={handlePaste}
          onFocus={handleFocus}
          disabled={disabled}
          variant={variant}
          size={size}
          slotProps={{
            input: {
              // MUI resolves this legacy escape hatch *after* the `htmlInput`
              // slot below, so the id has to be set on both paths or it is
              // silently dropped - they address the same <input>, so the
              // attribute is never duplicated. See `helpers/testId`.
              inputProps: {
                maxLength: 1,
                "aria-label": `Digit ${i + 1}`,
                autoComplete: "new-password",
                "data-testid": buildTestId(testId, i),
                style: {
                  textAlign: "center",
                  padding: 0,
                },
              },
            },
            htmlInput: {
              "data-testid": buildTestId(testId, i),
              sx: {
                textAlign: "center",
                fontSize: size === "small" ? "1.25rem" : "1.5rem",
                fontWeight: 600,
                padding: size === "small" ? "10px" : "14px",
              },
            },
          }}
          sx={{
            width: size === "small" ? 40 : 48,
            "& .MuiInputBase-root": {
              width: size === "small" ? 40 : 48,
              height: size === "small" ? 48 : 56,
            },
            "& input": {
              textAlign: "center",
              padding: 0,
            },
            // Remove number input spinners
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none",
              margin: 0,
            },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
          }}
        />
      );
    }

    return fields;
  };

  return (
    <Box
      data-testid={testId}
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "center",
        justifyContent: "center",
        direction: i18n.language === "ar" ? "rtl" : "ltr",
        ...sx,
      }}
    >
      {renderFields()}
    </Box>
  );
};
